import { useMemo } from 'react'
import { useGetBrandIdentity } from '@/features/brand-identity/hooks/use-brand-identity-api'
import { useGetBrandVoices } from '@/features/brand-voice/hooks/use-brand-voice-api'
import { useGetCompanyData } from '@/features/organization-configuration/hooks/use-get-company-data'
import { useOrganizationWebsites } from '@/features/organization-configuration/hooks/use-organization-websites'
import { useCurrentOrganization } from '@/hooks/use-current-organization'
import {
  LOCAL_STORAGE_SCOPE,
  useLocalStoragePersistence,
} from '@/hooks/use-local-storage-persistence'
import type { SetupProgressData, SetupStep } from '../types/setup-progress.types'

const COMPANY_DATA_FIELDS = [
  'industry',
  'companySize',
  'positioning',
  'problemSolved',
  'valueProposition',
  'objectives',
] as const

const BRAND_IDENTITY_FIELDS = [
  'logoUrl',
  'primaryColor',
  'primaryFont',
  'brandGuidelines',
] as const

function countFilledFields(
  data: Record<string, unknown> | null | undefined,
  fields: readonly string[],
): number {
  if (!data) return 0
  return fields.reduce((count, field) => {
    const value = data[field]
    if (value !== null && value !== undefined && value !== '') {
      return count + 1
    }
    return count
  }, 0)
}

export function useSetupProgress(): SetupProgressData {
  const { organization } = useCurrentOrganization()
  const organizationId = organization?.id ?? ''

  const websitesQuery = useOrganizationWebsites()
  const companyDataQuery = useGetCompanyData(organizationId)
  const brandIdentityQuery = useGetBrandIdentity()
  const brandVoicesQuery = useGetBrandVoices()

  const { state: isDismissed, updateState: setDismissed } = useLocalStoragePersistence({
    key: 'setup-progress-dismissed',
    defaultValue: false,
    scope: LOCAL_STORAGE_SCOPE.GLOBAL,
  })

  const isLoading =
    websitesQuery.isLoading || companyDataQuery.isLoading || brandIdentityQuery.isLoading || brandVoicesQuery.isLoading

  const steps: SetupStep[] = useMemo(() => {
    // Websites completion
    const websitesCount = websitesQuery.data?.length ?? 0
    const websitesRatio = websitesCount >= 1 ? 1 : 0

    // Company Data completion
    const companyFilledCount = countFilledFields(
      companyDataQuery.data as Record<string, unknown> | undefined,
      COMPANY_DATA_FIELDS,
    )
    const companyRatio = COMPANY_DATA_FIELDS.length > 0 ? companyFilledCount / COMPANY_DATA_FIELDS.length : 0

    // Brand Identity completion
    const brandIdentityFilledCount = countFilledFields(
      brandIdentityQuery.data as Record<string, unknown> | undefined,
      BRAND_IDENTITY_FIELDS,
    )
    const brandIdentityRatio =
      BRAND_IDENTITY_FIELDS.length > 0 ? brandIdentityFilledCount / BRAND_IDENTITY_FIELDS.length : 0

    // Brand Voice completion
    const voicesCount = brandVoicesQuery.data?.length ?? 0
    const brandVoiceRatio = voicesCount >= 1 ? 1 : 0

    return [
      {
        id: 'websites',
        isComplete: websitesRatio >= 1,
        completionRatio: websitesRatio,
        to: '/company-settings?tab=websites',
      },
      {
        id: 'companyData',
        isComplete: companyRatio >= 0.5,
        completionRatio: companyRatio,
        to: '/company-settings?tab=company-data',
      },
      {
        id: 'brandIdentity',
        isComplete: brandIdentityRatio >= 0.5,
        completionRatio: brandIdentityRatio,
        to: '/company-settings?tab=brand-identity',
      },
      {
        id: 'brandVoice',
        isComplete: brandVoiceRatio >= 1,
        completionRatio: brandVoiceRatio,
        to: '/company-settings?tab=brand-voices',
      },
    ]
  }, [websitesQuery.data, companyDataQuery.data, brandIdentityQuery.data, brandVoicesQuery.data])

  const overallPercentage = useMemo(() => {
    if (steps.length === 0) return 0
    const completedCount = steps.filter((s) => s.isComplete).length
    return Math.round((completedCount / steps.length) * 100)
  }, [steps])

  const isFullyComplete = steps.length > 0 && steps.every((s) => s.isComplete)

  const dismiss = () => setDismissed(true)

  return {
    steps,
    overallPercentage,
    isFullyComplete,
    isLoading,
    isDismissed,
    dismiss,
  }
}
